import React, {

  createContext,

  useContext,

  useEffect,

  useMemo,

  useState,

  type ReactNode,

} from "react";

import { ContentSourceIndicator } from "./ContentSourceIndicator";

import { loadGeniusContent } from "./loadContent";

import type { ContentLoadMeta } from "./contentSource";

import {

  buildNavItems,

  getOlympiadByRoute,

  getOlympiadPageSections,

  getPageSection,

  getSharedSection,

} from "./contentUtils";

import type { ContentSection, GeniusContent, NavItem, OlympiadEntry } from "./types";



interface GeniusContentContextValue {

  content: GeniusContent;

  loadMeta: ContentLoadMeta;

  navItems: NavItem[];

  getSection: (pageId: string, sectionId: string) => ContentSection | undefined;

  getShared: (key: string) => Record<string, unknown>;

  getOlympiad: (route: string) => OlympiadEntry | undefined;

  getOlympiadSections: (olympiadId: string) => ContentSection[];

}



const GeniusContentContext = createContext<GeniusContentContextValue | null>(null);



export const GeniusContentProvider: React.FC<{ children: ReactNode }> = ({

  children,

}) => {

  const [content, setContent] = useState<GeniusContent | null>(null);

  const [loadMeta, setLoadMeta] = useState<ContentLoadMeta | null>(null);



  useEffect(() => {

    loadGeniusContent().then(({ content: loaded, meta }) => {

      setContent(loaded);

      setLoadMeta(meta);

    });

  }, []);



  const value = useMemo<GeniusContentContextValue | null>(() => {

    if (!content || !loadMeta) return null;



    return {

      content,

      loadMeta,

      navItems: buildNavItems(content),

      getSection: (pageId, sectionId) => getPageSection(content, pageId, sectionId),

      getShared: (key) => getSharedSection(content, key),

      getOlympiad: (route) => getOlympiadByRoute(content, route),

      getOlympiadSections: (olympiadId) => getOlympiadPageSections(content, olympiadId),

    };

  }, [content, loadMeta]);



  if (!value) {

    return (

      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>

        <div className="spinner-border text-light" role="status">

          <span className="visually-hidden">Loading content...</span>

        </div>

      </div>

    );

  }



  return (

    <GeniusContentContext.Provider value={value}>

      {children}

      <ContentSourceIndicator meta={value.loadMeta} />

    </GeniusContentContext.Provider>

  );

};



export function useGeniusContent(): GeniusContentContextValue {

  const ctx = useContext(GeniusContentContext);

  if (!ctx) {

    throw new Error("useGeniusContent must be used within GeniusContentProvider");

  }

  return ctx;

}



export function useContentSource(): ContentLoadMeta {

  return useGeniusContent().loadMeta;

}



export function usePageSection<T extends ContentSection = ContentSection>(

  pageId: string,

  sectionId: string

): T {

  const { getSection } = useGeniusContent();

  const section = getSection(pageId, sectionId);

  if (!section) {

    throw new Error(`Missing content section: ${pageId}.${sectionId}`);

  }

  return section as T;

}



export function useSharedContent<T extends Record<string, unknown> = Record<string, unknown>>(

  key: string

): T {

  const { getShared } = useGeniusContent();

  return getShared(key) as T;

}



export function useNavItems(): NavItem[] {

  return useGeniusContent().navItems;

}



export function useOlympiads(): OlympiadEntry[] {

  return useGeniusContent().content.olympiads;

}


